import {WakeletHeader} from "../wakelet-header/WakeletHeader";
import {FC, useState} from "react";
import {inputClass} from "../form/input-class";
import {FieldGroup, ValidationError} from "../form/ValidationError";
import {Button, Checkbox, Dialog} from "@wakelet/ui-kit";
import * as yup from "yup";
import {FieldErrors, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {isOver12} from "./age-check";
import {IconChevronLeft} from "../icons/icons";
import {nanoid} from "nanoid";
import {Navigate} from "react-router-dom";
import {PasswordInput} from "../form/PasswordInput";
import {getWakeletUrl, isDev} from "../../env/env";
import {UseFormSetValue} from "react-hook-form/dist/types/form";
import {translateKey} from "../language/translate";
import {Captcha} from "../captcha/Captcha";
import {useSignUpWithEmail} from "./useSignUpWithEmail";
import {Spinner} from "../loading/Spinner";
import {
  gaBackButtonClicked,
  gaCreateAccountButtonClicked,
  gaEmailUnavailable,
  gaValidationError
} from "../../analytics/ga";
import {useNavigate} from "react-router";
import {FieldLabel, RequiredFieldsMessage} from "../form/FieldLabel";
import _debounce from "lodash/debounce";

export type EmailSignUpValues = {
  name: string
  email: string
  password: string
  dateOfBirth: string
  terms: boolean
  serverError: void
}

// create account seems to be triggering twice, just swallow any multiple submissions
const debouncedGACreateAccountButtonClicked = _debounce(gaCreateAccountButtonClicked, 500, {leading: true});

const invalidFormatMsg = translateKey("account:validation.dob");

const schema = yup.object({
  name: yup.string().trim().required(translateKey("account:validation.name")),
  email: yup.string().trim().email(translateKey("account:validation.email")).
    required(translateKey("account:validation.email")),
  password: yup.string().required(translateKey("account:validation.password")).
    min(8, translateKey("account:validation.password")),
  dateOfBirth: yup.string().required(invalidFormatMsg).typeError(invalidFormatMsg),
  terms: yup.boolean().oneOf([true], "You must accept the terms and conditions to proceed")
}).required();

const BackButton = () => {
  const navigate = useNavigate();
  const goBack = () => {
    gaBackButtonClicked();
    navigate(-1);
  }
  return (
    <div className="relative">
      <div className="absolute -top-4 -left-6">
        <Button onClick={goBack}
                className="m-4"
                variant="secondary"
                aria-label={translateKey("common:buttons.back")}
                data-testid="back-btn"
                data-id="email-back"
        >
          <Button.Label icon={<IconChevronLeft/>}/>
        </Button>
      </div>
    </div>
  );
};

const populateWithTestData = (setValue: UseFormSetValue<EmailSignUpValues>) => {
  setValue("name", `Mr ${nanoid(5)}`)
  setValue("email", `${nanoid(3)}@wakelet.com`)
  setValue("password", nanoid())
  setValue("dateOfBirth", "2001-05-04");
  setValue("terms", true);
}

export const SignUpWithEmailForm: FC = () => {
  const [showAgeAlert, setShowAgeAlert] = useState<boolean>();
  const {showCaptcha, isSubmitting, signUp, setCaptchaToken, error: signUpError} = useSignUpWithEmail();
  const {register, handleSubmit, formState: {errors}, setValue} = useForm<EmailSignUpValues>(
    {
      resolver: yupResolver(schema),
      defaultValues: {terms: false}
    },
  );
  emitGaValidationEvent(errors)
  if (signUpError?.firebaseCode === "auth/email-already-in-use") {
    gaEmailUnavailable();
    return <Navigate to="/link-account"/>
  }
  const signUpErrorMessage = signUpError ? translateKey(signUpError.i18nKey.key) : "";

  const doSubmit = (values: EmailSignUpValues) => {

    debouncedGACreateAccountButtonClicked();
    if (!isOver12(new Date(values.dateOfBirth))) {
      setShowAgeAlert(true)
      return
    }
    signUp(values);
  };
  const onChange = (value: boolean) => setValue("terms", value);
  const mashedUpTermProps = {...(register("terms")), onChange};
  return <div className="flex flex-col w-full">
    <BackButton/>
    <WakeletHeader message={translateKey("account:signup.title")}/>
    {
      isDev() &&
      <div className="w-full py-2">
        <Button variant="negative" className="w-full" onClick={() => populateWithTestData(setValue)} type="button">generate
          credentials</Button>
      </div>
    }
    <form onSubmit={handleSubmit(doSubmit)} aria-describedby={signUpErrorMessage && "sign-up-error"}>
      <FieldGroup>
        <ValidationError textCenter={true} message={signUpErrorMessage} id="sign-up-error"/>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="name">{translateKey("account:placeholders.name")}</FieldLabel>
        <input id="name"
               type="text"
               placeholder={translateKey("account:placeholders.name")}
               className={inputClass(errors.name)}
               {...register("name")}
               aria-required={true}
               data-testid="name-field"
               data-id="email-signup-name"
               aria-invalid={!!errors.name}
               aria-describedby={errors.name?.message ? "name-error" : undefined}
        />
        <ValidationError message={errors.name?.message} id="name-error"/>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="email">{translateKey("account:emailAddress")}</FieldLabel>
        <input id="email"
               type="text"
               placeholder={translateKey("account:placeholders.email")}
               className={inputClass(errors.email)}
               {...register("email")}
               autoComplete="username"
               aria-required={true}
               data-testid="email-field"
               data-id="email-signup-email"
               aria-invalid={!!errors.email}
               aria-describedby={errors.email?.message ? "email-error" : undefined}
        />
        <ValidationError message={errors.email?.message} id="email-error"/>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="password">{translateKey("account:placeholders.password")}</FieldLabel>
        <PasswordInput
          id="password"
          placeholder={translateKey("account:placeholders.password")}
          className={inputClass(errors.password)}
          {...register("password")}
          autoComplete="new-password"
          data-testid="password-field"
          data-id="email-signup-password"
          aria-invalid={!!errors.password}
          aria-describedby={errors.password?.message ? "password-error" : undefined}
        />
        <ValidationError message={errors.password?.message} id="password-error"/>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="date-of-birth">{translateKey("account:signup.dob")}</FieldLabel>
        <input id="date-of-birth"
               type="date"
               className={inputClass(errors.dateOfBirth)}
               {...register("dateOfBirth")}
               data-testid="date-of-birth-field"
               data-id="email-signup-dob"
               aria-invalid={!!errors.dateOfBirth}
               aria-required={true}
               autoComplete="bday"
               aria-describedby={errors.dateOfBirth?.message ? "date-of-birth-error" : undefined}
        />
        <ValidationError message={errors.dateOfBirth?.message} id="date-of-birth-error"/>
      </FieldGroup>
      <RequiredFieldsMessage/>
      <FieldGroup>
        <div className="flex gap-2 items-center p-3">
          <Checkbox id="terms-checkbox"
                    label={translateKey("account:signup.accept")}
                    hideLabel
                    {...mashedUpTermProps}
                    aria-label={translateKey("account:signup.accept")}
                    aria-required={true}
                    aria-invalid={!!errors.terms}
                    aria-describedby={errors.terms?.message ? "terms-error" : undefined}
                    data-testid="terms-field"
                    data-id="agree-terms"
          />
          <label htmlFor="terms-checkbox">
            By checking this box you agree to Wakelet&apos;s{" "}
            <span className="font-semibold hover:underline">
                <a data-id="terms-signup" href="https://wakelet.com/terms.html">Terms</a>,{" "}
              </span>
            <span className="font-semibold hover:underline">
                <a data-id="privacy-signup" href="https://wakelet.com/privacy.html">Privacy</a>{" "}
              </span>
            and{" "}
            <span className="font-semibold hover:underline">
                <a data-id="rules-signup" href="https://wakelet.com/rules.html">Rules</a>.
              </span>
          </label>
        </div>
        <ValidationError message={errors.terms?.message} id="terms-error"/>
      </FieldGroup>
      <div className={`flex justify-center ${showCaptcha && "mb-5"}`}>
        <Captcha tokenCallback={(token) => setCaptchaToken(token)} visible={showCaptcha}/>
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-btn"
              data-id="signup-email-confirm">
        {!isSubmitting && <Button.Label text={translateKey("account:signup.email.button")}/>}
        {isSubmitting && <Button.Label icon={<Spinner/>}/>}
      </Button>
    </form>
    <Dialog title={translateKey("account:signup.age.restriction.label")} open={showAgeAlert}
            onClose={() => setShowAgeAlert(false)}>
      <p>You must be aged 13 years or older to sign up to Wakelet. Ask your teacher to create a student account for you
        to continue.</p>

      <div className="self-end">
        <Button.Style>
          <a href={getWakeletUrl()}>
            <Button.Label text={translateKey("common:buttons.returnHome")}/>
          </a>
        </Button.Style>
      </div>

    </Dialog>
  </div>;

};

function emitGaValidationEvent(errors: FieldErrors<EmailSignUpValues>) {
  if (errors.name) {
    gaValidationError("name");
  }
  if (errors.email) {
    gaValidationError("email_address");
  }
  if (errors.password) {
    gaValidationError("password");
  }
  if (errors.dateOfBirth) {
    gaValidationError("date_of_birth");
  }
}
